import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Key, Link as LinkIcon, Database, Box, TrendingUp, Sigma } from 'lucide-react';
import { LocalStarModel } from '../../../../types';

interface StarSchemaDiagramProps {
  starModel: LocalStarModel;
}

interface Position {
  x: number;
  y: number;
}

interface RelationshipPath {
  id: string;
  path: string;
  from: { x: number; y: number };
  to: { x: number; y: number };
  cardinality: string;
  factName: string;
  dimensionName: string;
}

export const StarSchemaDiagram: React.FC<StarSchemaDiagramProps> = ({ starModel }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredTable, setHoveredTable] = useState<string | null>(null);
  const [hoveredRelationship, setHoveredRelationship] = useState<string | null>(null);
  const [relationshipPaths, setRelationshipPaths] = useState<RelationshipPath[]>([]);

  const factRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const dimRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (dimensions.width === 0 || starModel.facts.length === 0) return;

    const timer = setTimeout(() => {
      calculateRelationshipPaths();
    }, 150);

    return () => clearTimeout(timer);
  }, [dimensions, starModel, hoveredTable]);

  const calculateRelationshipPaths = () => {
    const paths: RelationshipPath[] = [];

    starModel.relationships.forEach((rel, index) => {
      const factEl = factRefs.current.get(rel.fact);
      const dimEl = dimRefs.current.get(rel.dimension);

      if (!factEl || !dimEl) return;

      const factBounds = factEl.getBoundingClientRect();
      const dimBounds = dimEl.getBoundingClientRect();
      const containerBounds = containerRef.current?.getBoundingClientRect();

      if (!containerBounds) return;

      const factCenter = {
        x: factBounds.left + factBounds.width / 2 - containerBounds.left,
        y: factBounds.top + factBounds.height / 2 - containerBounds.top,
      };

      const dimCenter = {
        x: dimBounds.left + dimBounds.width / 2 - containerBounds.left,
        y: dimBounds.top + dimBounds.height / 2 - containerBounds.top,
      };

      const factEdge = getEdgePoint(factBounds, dimCenter, containerBounds);
      const dimEdge = getEdgePoint(dimBounds, factCenter, containerBounds);

      const dx = dimEdge.x - factEdge.x;
      const dy = dimEdge.y - factEdge.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const controlOffset = Math.min(distance * 0.25, 50);

      const path = `M ${factEdge.x} ${factEdge.y} C ${factEdge.x + dx * 0.25} ${
        factEdge.y + controlOffset
      }, ${dimEdge.x - dx * 0.25} ${dimEdge.y - controlOffset}, ${dimEdge.x} ${dimEdge.y}`;

      paths.push({
        id: `${rel.fact}-${rel.dimension}-${index}`,
        path,
        from: factEdge,
        to: dimEdge,
        cardinality: rel.cardinality,
        factName: rel.fact,
        dimensionName: rel.dimension,
      });
    });

    setRelationshipPaths(paths);
  };

  const getEdgePoint = (
    bounds: DOMRect,
    targetPoint: Position,
    containerBounds: DOMRect
  ): Position => {
    const centerX = bounds.left + bounds.width / 2 - containerBounds.left;
    const centerY = bounds.top + bounds.height / 2 - containerBounds.top;

    const dx = targetPoint.x - centerX;
    const dy = targetPoint.y - centerY;
    const angle = Math.atan2(dy, dx);

    const halfWidth = bounds.width / 2;
    const halfHeight = bounds.height / 2;

    const slope = Math.abs(Math.tan(angle));
    let x = centerX;
    let y = centerY;

    if (slope < halfHeight / halfWidth) {
      x = centerX + Math.sign(dx) * halfWidth;
      y = centerY + Math.sign(dy) * halfWidth * slope;
    } else {
      x = centerX + Math.sign(dx) * halfHeight / slope;
      y = centerY + Math.sign(dy) * halfHeight;
    }

    return { x, y };
  };

  const calculateLayout = () => {
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    const baseRadius = Math.min(dimensions.width, dimensions.height) * 0.32;

    const dimensionCount = starModel.dimensions.length;
    const radius = dimensionCount > 0 ? baseRadius + (dimensionCount > 6 ? 40 : 0) : 0;

    return {
      centerX,
      centerY,
      radius,
    };
  };

  const layout = calculateLayout();

  // Show message if no facts
  if (starModel.facts.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[600px] bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-xl border border-slate-700/50">
        <div className="text-center p-8 max-w-lg">
          <div className="w-20 h-20 bg-slate-800/60 rounded-xl flex items-center justify-center mx-auto mb-6 border border-slate-700/50">
            <Database className="w-10 h-10 text-slate-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-300 mb-3">No Fact Tables Detected</h3>
          <p className="text-sm text-slate-400 mb-2">
            The star schema requires at least one fact table to visualize the relationships.
          </p>
          <p className="text-xs text-slate-500 mb-6">
            {starModel.dimensions.length > 0 
              ? `Found ${starModel.dimensions.length} dimension table(s) but no facts.`
              : 'No facts or dimensions were found in the analyzed data.'}
          </p>
          <div className="p-4 bg-slate-800/40 rounded-lg border border-slate-700/50 text-left">
            <p className="text-xs text-slate-400 leading-relaxed">
              <span className="text-blue-400 font-semibold">💡 Tip:</span> A fact table typically contains:
            </p>
            <ul className="text-xs text-slate-500 mt-2 ml-4 space-y-1">
              <li>• Numeric measures (sales, amounts, quantities)</li>
              <li>• Foreign keys linking to dimension tables</li>
              <li>• Transaction or event data</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full min-h-[700px] bg-gradient-to-br from-slate-900/30 to-slate-800/40 rounded-xl overflow-hidden border border-slate-700/40"
    >
      {/* SVG Layer for Relationships */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1 }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="8"
            markerHeight="8"
            refX="7"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" className="fill-slate-500" />
          </marker>

          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {relationshipPaths.map((rel) => {
          const isHovered =
            hoveredRelationship === rel.id ||
            hoveredTable === rel.factName ||
            hoveredTable === rel.dimensionName;

          return (
            <g key={rel.id}>
              <motion.path
                d={rel.path}
                fill="none"
                stroke={isHovered ? 'rgba(148, 163, 184, 0.7)' : 'rgba(100, 116, 139, 0.3)'}
                strokeWidth={isHovered ? 2 : 1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                markerEnd="url(#arrowhead)"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{
                  pathLength: 1,
                  opacity: 1,
                }}
                transition={{ duration: 0.6, delay: 0.3 }}
                filter={isHovered ? 'url(#glow)' : undefined}
                className="transition-all duration-200"
                style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
                onMouseEnter={() => setHoveredRelationship(rel.id)}
                onMouseLeave={() => setHoveredRelationship(null)}
              />

              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <foreignObject
                  x={(rel.from.x + rel.to.x) / 2 - 25}
                  y={(rel.from.y + rel.to.y) / 2 - 10}
                  width="50"
                  height="20"
                  style={{ pointerEvents: 'none' }}
                >
                  <div className="flex items-center justify-center">
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded shadow-sm backdrop-blur-sm transition-all duration-200 font-medium border ${
                        isHovered
                          ? 'bg-slate-700/90 text-slate-200 border-slate-500'
                          : 'bg-slate-800/70 text-slate-500 border-slate-700/50'
                      }`}
                    >
                      {rel.cardinality}
                    </span>
                  </div>
                </foreignObject>
              </motion.g>
            </g>
          );
        })}
      </svg>

      {/* Tables Layer */}
      <div className="relative w-full h-full" style={{ zIndex: 2 }}>
        {/* Fact Tables (Center) */}
        {starModel.facts.map((fact, index) => (
          <FactTableCard
            key={`fact-${index}`}
            fact={fact}
            position={{
              x: layout.centerX,
              y: layout.centerY,
            }}
            isHovered={hoveredTable === fact.name}
            onHover={() => setHoveredTable(fact.name)}
            onLeave={() => setHoveredTable(null)}
            ref={(el) => {
              if (el) factRefs.current.set(fact.name, el);
            }}
          />
        ))}

        {/* Dimension Tables (Radial) */}
        {starModel.dimensions.map((dimension, index) => {
          const angle = (2 * Math.PI * index) / starModel.dimensions.length - Math.PI / 2;
          const x = layout.centerX + layout.radius * Math.cos(angle);
          const y = layout.centerY + layout.radius * Math.sin(angle);

          return (
            <DimensionTableCard
              key={`dim-${index}`}
              dimension={dimension}
              position={{ x, y }}
              isHovered={hoveredTable === dimension.name}
              onHover={() => setHoveredTable(dimension.name)}
              onLeave={() => setHoveredTable(null)}
              delay={0.1 + index * 0.04}
              ref={(el) => {
                if (el) dimRefs.current.set(dimension.name, el);
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

// Fact Table Card
interface FactTableCardProps {
  fact: LocalStarModel['facts'][0];
  position: Position;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
}

const FactTableCard = React.forwardRef<HTMLDivElement, FactTableCardProps>(
  ({ fact, position, isHovered, onHover, onLeave }, ref) => {
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: isHovered ? 1.02 : 1 }}
        transition={{ duration: 0.2 }}
        className="absolute pointer-events-auto"
        style={{
          left: position.x,
          top: position.y,
          transform: 'translate(-50%, -50%)',
        }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
      >
        <div
          className={`
            rounded-lg backdrop-blur-sm shadow-xl border
            transition-all duration-200 min-w-[260px] max-w-[300px]
            ${
              isHovered
                ? 'bg-slate-800/95 border-slate-600 shadow-2xl ring-1 ring-slate-500/50'
                : 'bg-slate-800/85 border-slate-700/60 shadow-slate-900/50'
            }
          `}
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-slate-700/50 bg-gradient-to-r from-blue-500/5 to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-500/10 rounded flex items-center justify-center border border-blue-500/20">
                <Database className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-slate-100 leading-tight truncate">
                  {fact.name}
                </h3>
                <p className="text-[9px] text-slate-500 uppercase tracking-wider font-medium">
                  Fact Table
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-3 py-2 space-y-2">
            {/* Grain Keys */}
            {fact.grain.length > 0 && (
              <div>
                <p className="text-[9px] text-slate-500 uppercase tracking-wide font-semibold mb-1">
                  Keys <span className="text-slate-600">({fact.grain.length})</span>
                </p>
                <div className="space-y-0.5">
                  {fact.grain.slice(0, 4).map((key, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-900/40 border border-slate-700/30"
                    >
                      <Key className="w-2.5 h-2.5 text-amber-400 flex-shrink-0" />
                      <span className="text-[10px] text-slate-300 font-mono truncate">{key}</span>
                    </div>
                  ))}
                  {fact.grain.length > 4 && (
                    <p className="text-[9px] text-slate-600 pl-4">
                      +{fact.grain.length - 4} more keys
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Measures */}
            <div>
              <p className="text-[9px] text-slate-500 uppercase tracking-wide font-semibold mb-1">
                Measures <span className="text-slate-600">({fact.measures.length})</span>
              </p>
              <div className="space-y-0.5 max-h-32 overflow-y-auto custom-scrollbar pr-0.5">
                {fact.measures.map((measure, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-slate-900/40 border border-slate-700/30"
                  >
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <Sigma className="w-2.5 h-2.5 text-sky-400 flex-shrink-0" />
                      <span className="text-[10px] text-slate-300 truncate">{measure.name}</span>
                    </div>
                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-400 uppercase font-semibold">
                      {measure.aggregation}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          {fact.sourceTable && (
            <div className="px-3 py-1.5 border-t border-slate-700/30 bg-slate-900/20">
              <p className="text-[8px] text-slate-600">
                <span className="text-slate-500">Source:</span> {fact.sourceTable}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    );
  }
);

FactTableCard.displayName = 'FactTableCard';

// Dimension Table Card
interface DimensionTableCardProps {
  dimension: LocalStarModel['dimensions'][0];
  position: Position;
  isHovered: boolean;
  onHover: () => void;
  onLeave: () => void;
  delay?: number;
}

const DimensionTableCard = React.forwardRef<HTMLDivElement, DimensionTableCardProps>(
  ({ dimension, position, isHovered, onHover, onLeave, delay = 0 }, ref) => {
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: isHovered ? 1.02 : 1 }}
        transition={{ duration: 0.2, delay }}
        className="absolute pointer-events-auto"
        style={{
          left: position.x,
          top: position.y,
          transform: 'translate(-50%, -50%)',
        }}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
      >
        <div
          className={`
            rounded-lg backdrop-blur-sm shadow-lg border
            transition-all duration-200 min-w-[180px] max-w-[220px]
            ${
              isHovered
                ? 'bg-slate-800/95 border-slate-600 ring-1 ring-purple-500/40'
                : 'bg-slate-800/75 border-slate-700/50'
            }
          `}
        >
          {/* Header */}
          <div className="px-2.5 py-1.5 border-b border-slate-700/40 bg-gradient-to-r from-purple-500/5 to-transparent">
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 bg-purple-500/10 rounded flex items-center justify-center border border-purple-500/20">
                <Box className="w-3 h-3 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-semibold text-slate-100 leading-tight truncate">
                  {dimension.name}
                </h3>
                <p className="text-[8px] text-slate-500 uppercase tracking-wider font-medium">
                  Dimension
                </p>
              </div>
            </div>
          </div>

          {/* Attributes */}
          <div className="px-2.5 py-2">
            <p className="text-[8px] text-slate-500 uppercase tracking-wide font-semibold mb-1">
              Attributes <span className="text-slate-600">({dimension.attributes.length})</span>
            </p>
            <div className="space-y-0.5 max-h-28 overflow-y-auto custom-scrollbar">
              {dimension.attributes.slice(0, 8).map((attr, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-slate-900/30 border border-slate-700/20"
                >
                  <div className="w-1 h-1 rounded-full bg-slate-600 flex-shrink-0" />
                  <span className="text-[9px] text-slate-300 truncate flex-1">{attr.name}</span>
                  <span className="text-[7px] text-slate-600 uppercase">{attr.type}</span>
                </div>
              ))}
              {dimension.attributes.length > 8 && (
                <p className="text-[8px] text-slate-600 pl-3">
                  +{dimension.attributes.length - 8} more
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          {dimension.sourceTable && (
            <div className="px-2.5 py-1 border-t border-slate-700/20 bg-slate-900/10">
              <p className="text-[7px] text-slate-600">
                <span className="text-slate-500">Source:</span> {dimension.sourceTable}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    );
  }
);

DimensionTableCard.displayName = 'DimensionTableCard';

export default StarSchemaDiagram;
